import { type FC } from "react";

import { Tabs } from "@mantine/core";
import { Spacing } from "@snapchat/snap-design-system";

import { RouteEntities } from "../../core/routes/relativeRoutes";
import { useOrganicStats } from "../hooks";
import { useInsightsSelectedTab } from "../hooks/useInsightsSelectedTab";
import { type TabVisibilityMap } from "../models";
import { isLensInsightsTab } from "../utils/insightsNavigation";

import { tabs } from "./LensInsightsTabs.module.css";
import { AudienceTab } from "./audience-tab";
import { ConversionTab } from "./conversion-tab";
import { EngagementTab } from "./engagement-tab";
import { EventInsightsTab } from "./event-insights-tab";
import { FunnelTab } from "./funnel-tab";
import {
    TabNameAudience,
    TabNameConversion,
    TabNameCreatorPayoutMonetization,
    TabNameEngagement,
    TabNameEventInsights,
    TabNameFunnelAnalysis,
} from "./messages.generated";
import { PayoutMonetizationTab } from "./payout-monetization-tab";
import { ChartPaper, StatsEmptyStateContainer } from "./shared";

export interface LensInsightsTabsProps {
    notEnoughData: boolean;
    tabVisibilityMap: TabVisibilityMap;
}

const EmptyTabContainer: FC = () => (
    <ChartPaper padding={`${Spacing.XL}px`} margin={`${Spacing.LG}px 0 0 0`}>
        <StatsEmptyStateContainer isEmpty={true} />
    </ChartPaper>
);

const { Tab, List, Panel: TabPanel } = Tabs;

export const LensInsightsTabs: FC<LensInsightsTabsProps> = ({ notEnoughData, tabVisibilityMap }) => {
    const { loading: chartsLoading, chartsMetrics, chartsMetricsComputed } = useOrganicStats();
    const { resolvedTab, navigateToTab } = useInsightsSelectedTab();

    const handleTabChange = (value: string | null) => {
        if (isLensInsightsTab(value)) {
            navigateToTab(value);
        }
    };

    const shouldShowAudience = tabVisibilityMap[RouteEntities.Audience];
    const shouldShowEventInsights = tabVisibilityMap[RouteEntities.EventInsights];
    const shouldShowConversion = tabVisibilityMap[RouteEntities.Conversion];
    const shouldShowFunnelTab = tabVisibilityMap[RouteEntities.Funnel];
    const shouldShowMonetization = tabVisibilityMap[RouteEntities.Monetization];

    return (
        <Tabs
            value={resolvedTab ?? RouteEntities.Audience}
            onChange={handleTabChange}
            className={tabs}
            keepMounted={false}
        >
            <List>
                {shouldShowAudience && (
                    <Tab value={RouteEntities.Audience} className="subheading-sh300">
                        <TabNameAudience />
                    </Tab>
                )}
                <Tab value={RouteEntities.Engagement} className="subheading-sh300">
                    <TabNameEngagement />
                </Tab>
                {shouldShowEventInsights && (
                    <Tab value={RouteEntities.EventInsights} className="subheading-sh300">
                        <TabNameEventInsights />
                    </Tab>
                )}
                {shouldShowConversion && (
                    <Tab value={RouteEntities.Conversion} className="subheading-sh300">
                        <TabNameConversion />
                    </Tab>
                )}
                {shouldShowFunnelTab && (
                    <Tab value={RouteEntities.Funnel} className="subheading-sh300">
                        <TabNameFunnelAnalysis />
                    </Tab>
                )}
                {shouldShowMonetization && (
                    <Tab value={RouteEntities.Monetization} className="subheading-sh300">
                        <TabNameCreatorPayoutMonetization />
                    </Tab>
                )}
            </List>

            {shouldShowAudience && (
                <TabPanel value={RouteEntities.Audience}>
                    {notEnoughData ? (
                        <EmptyTabContainer />
                    ) : (
                        <AudienceTab chartsMetrics={chartsMetrics} loading={chartsLoading} />
                    )}
                </TabPanel>
            )}

            <TabPanel value={RouteEntities.Engagement}>
                {notEnoughData ? (
                    <EmptyTabContainer />
                ) : (
                    <EngagementTab chartsMetrics={chartsMetrics} loading={chartsLoading} />
                )}
            </TabPanel>

            {shouldShowEventInsights && (
                <TabPanel value={RouteEntities.EventInsights}>
                    {notEnoughData ? (
                        <EmptyTabContainer />
                    ) : (
                        <EventInsightsTab
                            chartMetrics={chartsMetrics}
                            chartMetricsComputed={chartsMetricsComputed}
                            loading={chartsLoading}
                        />
                    )}
                </TabPanel>
            )}

            {shouldShowConversion && (
                <TabPanel value={RouteEntities.Conversion}>
                    {notEnoughData ? (
                        <EmptyTabContainer />
                    ) : (
                        <ConversionTab
                            chartsMetrics={chartsMetrics}
                            chartsMetricsComputed={chartsMetricsComputed}
                            loading={chartsLoading}
                        />
                    )}
                </TabPanel>
            )}

            {shouldShowFunnelTab && (
                <TabPanel value={RouteEntities.Funnel}>
                    {notEnoughData ? <EmptyTabContainer /> : <FunnelTab />}
                </TabPanel>
            )}

            {shouldShowMonetization && (
                <TabPanel value={RouteEntities.Monetization}>
                    <PayoutMonetizationTab />
                </TabPanel>
            )}
        </Tabs>
    );
};
